import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { Icon, ICONS } from 'components/Icon';
import {
  NavigationHeader,
  NavigationHeaderProps,
} from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Pressable } from 'components/Pressable';
import { ProfileAvatarField } from 'components/ProfileAvatarField';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { Txt } from 'components/Txt';
import { useAlert } from 'hooks/useAlert';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { getFieldsHiddenByCriteria } from 'lib/fieldCriteria';
import {
  calculateAutoId,
  displayAutoId,
  getFieldGroupInfo,
  hasValue,
} from 'lib/fields';
import { GlobalStyles, theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import { useDispatch } from 'react-redux';
import { Field } from 'screens/AssetScreen/Field/Field';
import { MiniPlanAsset } from 'screens/FieldComponents/MiniPlanAsset';
import { MiniPlanAssessment } from 'screens/FieldComponents/MiniPlanAssessment';
import {
  doAssetLoadPlans,
  eventBarcodeSelected,
  eventPinSelected,
  OPEN_BARCODE_SCANNER,
  OPEN_BARCODE_VIEWER,
} from 'store/actions';
import { FieldTypes } from 'store/assets/types';
import { FieldGroup } from 'store/organisations/types';
import { PinNew, PinNewWithName } from 'store/plan/types';
import {
  AppContextEnum,
  AppContext,
  FieldResult,
  FieldInstance,
} from 'types/types';
import { API_ASE_IMAGE } from 'service/api/constants';
import { Resizer } from 'components/Resizer';
import { doAseLoadPlans } from 'store/assessments/actions';
import { selectUrlToken } from 'store/auth/selectors';
import { useSelector } from 'hooks/useSelector';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { randomKey, rbAddReferencePrefix } from 'lib/riskbase';
import { TxtAsterisk } from 'components/TxtAsterisk';

type Props = {
  context: AppContext;
  group: Omit<FieldGroup, 'fields'> & {
    calculatedInfo: ReturnType<typeof getFieldGroupInfo>;
  };
  fields: FieldGroup['fields'];
  editableFieldIds?: string[];
  requiredFieldIds?: string[];
  hiddenFieldIds?: string[];
  id: string;
  isNew?: boolean;
  isAdd?: boolean;
  /**
   * Use this to re-render a field after an external value change
   */
  fieldKeys: Record<string, string>;
  // createdTs: number;
  results: Record<string, FieldResult>;
  resultsOld?: Record<string, FieldResult>;
  resultsNew?: Record<string, FieldResult>;
  autoId: number;
  onChange: (
    fieldId: string,
    value: string | string[] | null,
    pin?: PinNew | '' | null,
  ) => void;
  onRevert?: (fieldId: string) => void;
  otherInstances: FieldInstance[];
  autoIdNext: number;
  onPressSaveButton?: () => void;
  onPressCancelButton?: () => void;
  onPressBackButton?: () => void;
  headerMenuRight?: NavigationHeaderProps['menuRight'];
  isInModal?: boolean;
  promptTitle?: string;
  prompt?: string;
  ComponentAfterReference?: React.ReactNode;
  ComponentAfterFields?: React.ReactNode;
  saveButtonText?: string;
  cancelButtonText?: string;

  reference: string | undefined;

  showInvalid: boolean;
};

type SpecialField = {
  defined: boolean;
  id: string;
  label: string;
  editable: boolean;
  required: boolean;
};

export const GlobalFieldRepeatableInstanceScreen: React.FC<Props> = ({
  context,
  group,
  fields,
  editableFieldIds = [],
  requiredFieldIds = [],
  hiddenFieldIds = [],
  id,
  isNew,
  isAdd,
  // createdTs,
  results,
  resultsOld,
  resultsNew,
  autoId,
  onChange,
  otherInstances,
  autoIdNext,
  onPressSaveButton,
  onPressCancelButton,
  onPressBackButton,
  headerMenuRight,
  isInModal,
  promptTitle,
  prompt,
  ComponentAfterReference,
  ComponentAfterFields,
  saveButtonText,
  cancelButtonText,
  fieldKeys,
  onRevert,
  reference,
  showInvalid,
}) => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const urlToken = useSelector(selectUrlToken);

  const { imageFieldId, pinFieldId, qrFieldId } = group.calculatedInfo;

  const eventKeyQrSelect = React.useRef(randomKey());
  const eventKeyPinSelect = React.useRef(randomKey());

  const handleOnChangeImage = React.useCallback(
    (fileId?: string) => {
      if (imageFieldId) {
        onChange(imageFieldId, fileId ? [fileId] : []);
      }
    },
    [onChange, imageFieldId],
  );

  const fieldsHaveCriteria = group.fieldIds.some(
    (fieldId) => !!fields[fieldId]?.criteria?.length,
  );

  const fieldIsHiddenByCriteria = React.useMemo(
    () =>
      fieldsHaveCriteria
        ? getFieldsHiddenByCriteria({
            fieldIds: group.fieldIds,
            fields,
            results,
          })
        : {},
    [fieldsHaveCriteria, group.fieldIds, fields, results],
  );

  const otherInstanceData = React.useMemo(
    () =>
      otherInstances.reduce<{
        pins: PinNewWithName[];
        textSuggestionsByField: Record<string, string[]>;
      }>(
        (acc, oInstance) =>
          Object.values(oInstance.results).reduce((acc1, result) => {
            if (oInstance.instanceId !== id) {
              const { fieldId } = result;
              const field = fields[fieldId];
              switch (field?.type) {
                case FieldTypes.PIN:
                  if (result.pin) {
                    acc1.pins.push({
                      planId: result.pin.planId,
                      x: result.pin.x,
                      y: result.pin.y,
                      id: `${oInstance.instanceId}-${result.fieldId}`,
                      name: group.disableAutoId
                        ? oInstance.reference || 'New'
                        : displayAutoId(
                            calculateAutoId(
                              oInstance.autoId,
                              oInstance.draftAutoId,
                              autoIdNext,
                            ),
                            group.autoIdZeroFill || 0,
                            group.autoIdPrefix || '',
                          ),
                      group: group.name,
                    });
                  }
                  break;
                case FieldTypes.TEXT:
                case FieldTypes.NOTE:
                  if (result.value) {
                    if (!acc1.textSuggestionsByField[fieldId]) {
                      acc1.textSuggestionsByField[fieldId] = [];
                    }
                    acc1.textSuggestionsByField[fieldId]!.push(
                      result.value as string,
                    );
                  }
                  break;
                default:
              }
            }
            return acc;
          }, acc),
        { pins: [], textSuggestionsByField: {} },
      ),
    [
      autoIdNext,
      group.autoIdPrefix,
      group.autoIdZeroFill,
      group.disableAutoId,
      fields,
      group.name,
      id,
      otherInstances,
    ],
  );

  const { imageField, qrField, pinField, fieldIdsForLayout } = React.useMemo(
    () =>
      Object.values(group.fieldIds).reduce<{
        imageField: SpecialField & { value: string };
        qrField: SpecialField & { value: string };
        pinField: SpecialField & { value: FieldResult['pin'] };
        fieldIdsForLayout: string[];
      }>(
        (acc, fieldId) => {
          const field = fields[fieldId]!;
          if (hiddenFieldIds.includes(field.id)) {
            return acc;
          }
          if (
            fieldIsHiddenByCriteria[fieldId] &&
            !hasValue(results[field.id])
          ) {
            return acc;
          }
          if (fieldId === imageFieldId) {
            acc.imageField = {
              defined: true,
              id: fieldId,
              label: field.label,
              editable: editableFieldIds.includes(fieldId),
              required: requiredFieldIds.includes(fieldId),
              value: (results[fieldId]?.value[0] || '') as string,
            };
          } else if (fieldId === qrFieldId) {
            acc.qrField = {
              defined: true,
              id: fieldId,
              label: field.label,
              editable: editableFieldIds.includes(fieldId),
              required: requiredFieldIds.includes(fieldId),
              value: (results[fieldId]?.value || '') as string,
            };
          } else if (fieldId === pinFieldId) {
            acc.pinField = {
              defined: true,
              id: fieldId,
              label: field.label,
              editable: editableFieldIds.includes(fieldId),
              required: requiredFieldIds.includes(fieldId),
              value: results[fieldId]?.pin || '',
            };
          } else {
            acc.fieldIdsForLayout.push(fieldId);
          }
          return acc;
        },
        {
          imageField: {
            defined: false,
            id: '',
            label: '',
            editable: false,
            required: false,
            value: '',
          },
          qrField: {
            defined: false,
            id: '',
            label: '',
            editable: false,
            required: false,
            value: '',
          },
          pinField: {
            defined: false,
            id: '',
            label: '',
            editable: false,
            required: false,
            value: '',
          },
          fieldIdsForLayout: [],
        },
      ),
    [
      group.fieldIds,
      fields,
      hiddenFieldIds,
      fieldIsHiddenByCriteria,
      results,
      imageFieldId,
      qrFieldId,
      pinFieldId,
      editableFieldIds,
      requiredFieldIds,
    ],
  );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventBarcodeSelected, (action) => {
        if (qrField.id && action.payload.key === eventKeyQrSelect.current) {
          onChange(qrField.id, action.payload.data);
        }
      }),
      doAddEvent(eventPinSelected, (action) => {
        if (pinField.id && action.payload.key === eventKeyPinSelect.current) {
          onChange(pinField.id, '', action.payload.data || '');
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [onChange, pinField, qrField]);

  const isShowingSpecialHeader =
    imageField.defined || qrField.defined || pinField.defined;

  return (
    <PageScrollView
      isInModal={!!isInModal}
      contentPaddingTop={isShowingSpecialHeader ? 0 : undefined}
      contentPaddingBottom={0}
      header={
        <NavigationHeader
          onPressSaveButton={onPressSaveButton}
          onPressCancelButton={onPressCancelButton}
          onPressBackButton={onPressBackButton}
          saveButtonText={saveButtonText}
          cancelButtonText={cancelButtonText}
          heading={`${isNew ? 'Add ' : isAdd ? 'Add ' : ''}${
            group.calculatedInfo.singularName
          }`}
          subheading={promptTitle}
          onPressSubheading={
            promptTitle && prompt ? () => alert({ text: prompt }) : undefined
          }
          subheadingIcon={
            promptTitle && prompt ? ICONS.CircleInfoLight : undefined
          }
          menuRight={headerMenuRight}
        />
      }
    >
      {isShowingSpecialHeader && (
        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-evenly',
            marginVertical: theme.paddingVertical,
            marginHorizontal: theme.paddingHorizontal,
          }}
        >
          {imageField.defined && (
            <View
              style={{
                minWidth: 135,
                maxWidth: 200,
                flex: 1,
                marginHorizontal: theme.paddingHorizontal,
              }}
            >
              <Resizer
                style={{
                  flex: 1,
                }}
                render={({ width }) => (
                  <>
                    <Txt
                      style={{
                        textAlign: 'center',
                        marginBottom: theme.buttonMargin,
                      }}
                      size="small"
                      weight="medium"
                    >
                      {imageField.label}
                      {imageField.required && <TxtAsterisk />}
                    </Txt>
                    <ProfileAvatarField
                      context={context}
                      size={width}
                      fileId={imageField.value}
                      onChange={handleOnChangeImage}
                      editable={imageField.editable}
                      removeConfirmTitle="Remove Image"
                      remoteUrl={
                        context.context === AppContextEnum.remoteAssessment
                          ? API_ASE_IMAGE(context.assessmentId, { urlToken })
                          : undefined
                      }
                      invalid={
                        showInvalid && imageField.required && !imageField.value
                      }
                    />
                  </>
                )}
              />
            </View>
          )}
          {qrField.defined && (qrField.editable || !!qrField.value) && (
            <View
              style={{
                minWidth: 80,
                maxWidth: 150,
                flex: 1,
                marginHorizontal: theme.paddingHorizontal,
              }}
            >
              <Resizer
                render={({ width }) => (
                  <>
                    <Txt
                      style={{
                        textAlign: 'center',
                        marginBottom: theme.buttonMargin,
                      }}
                      size="small"
                      weight="medium"
                    >
                      {qrField.label}
                      {qrField.required && <TxtAsterisk />}
                    </Txt>
                    <Pressable
                      style={[
                        {
                          width,
                          height: width,
                          borderRadius: 4,
                          alignItems: 'center',
                          justifyContent: 'center',
                        },
                        qrField.value
                          ? {
                              borderWidth: 1,
                              backgroundColor: qrField.value
                                ? 'white'
                                : 'transparent',
                              borderColor: qrField.value
                                ? theme.colors.blue
                                : 'transparent',
                            }
                          : undefined,
                        showInvalid && qrField.required && !qrField.value
                          ? GlobalStyles.invalidFieldOutline
                          : undefined,
                      ]}
                      hoveredStyle={{
                        backgroundColor: theme.colors.blue10,
                      }}
                      pressedStyle={{
                        backgroundColor: theme.colors.blue20,
                      }}
                      onPress={
                        qrField.value
                          ? () =>
                              dispatch(
                                OPEN_BARCODE_VIEWER({
                                  data: qrField.value,
                                  deleteKey: qrField.editable
                                    ? eventKeyQrSelect.current
                                    : undefined,
                                }),
                              )
                          : qrField.editable
                            ? () =>
                                dispatch(
                                  OPEN_BARCODE_SCANNER({
                                    selectionKey: eventKeyQrSelect.current,
                                  }),
                                )
                            : undefined
                      }
                    >
                      {qrField.value ? (
                        <QRCode
                          value={qrField.value}
                          size={width - 20}
                          backgroundColor="transparent"
                        />
                      ) : (
                        <>
                          <Icon
                            source={ICONS.QRCodeLight}
                            color={
                              qrField.editable
                                ? theme.colors.blue
                                : theme.colors.placeholder
                            }
                            style={{
                              marginBottom: theme.paddingHorizontalHalf,
                              marginTop: theme.paddingHorizontalHalf,
                            }}
                          />
                          {qrField.editable && (
                            <Txt style={{ color: theme.colors.blue }}>Set</Txt>
                          )}
                        </>
                      )}
                    </Pressable>
                  </>
                )}
              />
            </View>
          )}
          {pinField.defined && (pinField.editable || !!pinField.value) && (
            <View
              style={{
                minWidth: 80,
                maxWidth: 150,
                flex: 1,
                marginHorizontal: theme.paddingHorizontal,
              }}
            >
              <Resizer
                render={({ width }) => (
                  <>
                    <Txt
                      style={{
                        textAlign: 'center',
                        marginBottom: theme.buttonMargin,
                      }}
                      size="small"
                      weight="medium"
                    >
                      {pinField.label}
                      {pinField.required && <TxtAsterisk />}
                    </Txt>
                    <Pressable
                      style={[
                        {
                          width,
                          height: width,
                          borderRadius: 4,
                          alignItems: 'center',
                          justifyContent: 'center',
                        },
                        pinField.value
                          ? {
                              borderWidth: 1,
                              backgroundColor: pinField.value
                                ? 'white'
                                : 'transparent',
                              borderColor: pinField.value
                                ? theme.colors.blue
                                : 'transparent',
                            }
                          : undefined,
                        showInvalid && pinField.required && !pinField.value
                          ? GlobalStyles.invalidFieldOutline
                          : undefined,
                      ]}
                      hoveredStyle={{
                        backgroundColor: theme.colors.blue10,
                      }}
                      pressedStyle={{
                        backgroundColor: theme.colors.blue20,
                      }}
                      onPress={
                        pinField.editable || pinField.value
                          ? () =>
                              context.context === AppContextEnum.localAsset
                                ? dispatch(
                                    doAssetLoadPlans({
                                      assetId: context.assetId,
                                      pin: pinField.value || undefined,
                                      pins: otherInstanceData.pins,
                                      selectionKey: pinField.editable
                                        ? eventKeyPinSelect.current
                                        : undefined,
                                    }),
                                  )
                                : dispatch(
                                    doAseLoadPlans({
                                      pin: pinField.value || undefined,
                                      pins: otherInstanceData.pins,
                                      selectionKey: pinField.editable
                                        ? eventKeyPinSelect.current
                                        : undefined,
                                    }),
                                  )
                          : undefined
                      }
                    >
                      {pinField.value ? (
                        <View
                          style={{
                            width: width - 6,
                            height: width - 6,
                            backgroundColor: 'white',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 2,
                            opacity: 0.8,
                          }}
                        >
                          {context.context === AppContextEnum.localAsset ? (
                            <MiniPlanAsset
                              assetId={context.assetId}
                              planId={pinField.value.planId}
                              x={pinField.value.x}
                              y={pinField.value.y}
                              maxHeight={width - 6}
                              maxWidth={width - 6}
                            />
                          ) : (
                            <MiniPlanAssessment
                              planId={pinField.value.planId}
                              x={pinField.value.x}
                              y={pinField.value.y}
                              maxHeight={width - 6}
                              maxWidth={width - 6}
                            />
                          )}
                        </View>
                      ) : (
                        <>
                          <Icon
                            source={ICONS.LocationPinLight}
                            color={
                              pinField.editable
                                ? theme.colors.blue
                                : theme.colors.placeholder
                            }
                            style={{
                              marginBottom: theme.paddingHorizontalHalf,
                              marginTop: theme.paddingHorizontalHalf,
                            }}
                          />
                          {pinField.editable && (
                            <Txt
                              style={{
                                color: theme.colors.blue,
                              }}
                            >
                              Set
                            </Txt>
                          )}
                        </>
                      )}
                    </Pressable>
                  </>
                )}
              />
            </View>
          )}
        </View>
      )}

      {(!group.disableAutoId || reference !== undefined) && (
        <Card>
          <ViewWithSeparators horizontal>
            {reference ? (
              <CardRow
                label={rbAddReferencePrefix(reference)}
                icon={ICONS.RBPlectrumSolid}
                style={{ flex: 1 }}
              />
            ) : (
              <CardRow icon={ICONS.RBPlectrumSolid} style={{ flex: 1 }}>
                <Txt>
                  {rbAddReferencePrefix('')}
                  <Txt style={{ color: theme.colors.placeholder }}>000000</Txt>
                </Txt>
              </CardRow>
            )}
            {!group.disableAutoId && (
              <CardRow
                label={displayAutoId(
                  autoId,
                  group.autoIdZeroFill || 0,
                  group.autoIdPrefix || '',
                )}
                style={{ flex: 1 }}
              />
            )}
          </ViewWithSeparators>
        </Card>
      )}

      {ComponentAfterReference}

      <SeparatorHorizontal withMarginBottom />

      {fieldIdsForLayout.map((fieldId) => {
        const field = fields[fieldId]!;
        return (
          <Field
            key={fieldKeys[field.id] || `fk-${field.id}`}
            context={context}
            field={field}
            editable={editableFieldIds.includes(fieldId)}
            required={requiredFieldIds.includes(fieldId)}
            value={results[field.id]?.value}
            pin={results[field.id]?.pin || undefined}
            showRevert={!!resultsNew?.[field.id]}
            previousValue={resultsOld?.[field.id]?.value}
            previousPin={resultsOld?.[field.id]?.pin || undefined}
            onChange={onChange}
            onRevert={onRevert}
            textSuggestions={otherInstanceData.textSuggestionsByField[field.id]}
            pins={
              field.type === FieldTypes.PIN ? otherInstanceData.pins : undefined
            }
            shouldBeHidden={!!fieldIsHiddenByCriteria[fieldId]}
            onEsc={onPressCancelButton}
            instanceId={id}
            showInvalid={showInvalid}
          />
        );
      })}

      {ComponentAfterFields}
    </PageScrollView>
  );
};
